import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { SocialIcon } from '../icons/SocialIcon';
import { SocialMediaPostPreview } from './SocialMediaPostPreview';
import type { SocialPlatform } from '../icons/SocialIcon';
import type { Brand } from '../../types';
import { useTranslation } from 'react-i18next';

interface PreviewCardProps {
  content: string;
  platforms?: SocialPlatform[];
  selectedPlatforms?: SocialPlatform[];
  media?: File | null;
  mediaUrl?: string | null;
  mediaType?: 'image' | 'video' | null;
  existingMediaUrl?: string | null;
  existingMediaType?: 'image' | 'video' | null;
  format?: 'regular' | 'reel';
  selectedBrand?: string;
  brandName?: string;
  brandLogo?: string;
  brands?: Brand[];
}

export const PreviewCard: React.FC<PreviewCardProps> = ({
  content,
  platforms = [],
  selectedPlatforms = [],
  media = null,
  mediaUrl = null,
  mediaType = null,
  existingMediaUrl,
  existingMediaType,
  format = 'regular',
  selectedBrand,
  brandName,
  brandLogo,
  brands = []
}) => {
  const { t } = useTranslation();
  const brandObject = brands.find(brand => brand.id === selectedBrand);
  
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [activePreviewPlatform, setActivePreviewPlatform] = useState<SocialPlatform | null>(null);
  
  useEffect(() => {
    if (media) {
      const url = URL.createObjectURL(media);
      setObjectUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
    return undefined;
  }, [media]);
  
  const mediaObject = media 
    ? {
        type: media.type.startsWith('image/') ? 'image' as const : 'video' as const,
        url: objectUrl || '',
        uploadedAt: new Date().toISOString()
      } 
    : mediaUrl || existingMediaUrl 
      ? {
          type: (mediaType || existingMediaType || 'image') as 'image' | 'video',
          url: mediaUrl || existingMediaUrl || '',
          uploadedAt: new Date().toISOString()
        } 
      : undefined;
  
  const displayPlatforms = platforms.length > 0 ? platforms : selectedPlatforms;
  
  useEffect(() => {
    // If there are platforms to display
    if (displayPlatforms.length > 0) {
      // If there's no active platform OR the current active one is no longer in the list
      if (!activePreviewPlatform || !displayPlatforms.includes(activePreviewPlatform)) {
        setActivePreviewPlatform(displayPlatforms[0]); // Set to the first available one
      }
    } else {
      // If there are no platforms to display, clear the active platform
      setActivePreviewPlatform(null);
    }
  }, [displayPlatforms, activePreviewPlatform]); // Added activePreviewPlatform to dependency array for robustness, though displayPlatforms is the primary trigger
  
  const getTranslatedPlatformName = (platform: SocialPlatform): string => {
    const key = `platforms.${platform}`;
    const translated = t(key);
    if (translated && translated !== key) {
      return translated;
    }
    return platform.charAt(0).toUpperCase() + platform.slice(1);
  };

  return (
    <div className="bg-white rounded-xl border border-black shadow-lg p-4">
      <div className="flex items-center gap-3 mb-4">
        <Eye className="w-5 h-5 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">{t('cards.preview.title', 'Preview')}</h2>
      </div>
      
      {displayPlatforms.length > 0 ? (
        <>
          <div className="flex space-x-1 mb-4 border-b border-gray-200 overflow-x-auto">
            {displayPlatforms.map(platform => (
              <button
                key={platform}
                onClick={() => setActivePreviewPlatform(platform)}
                className={`py-2 px-3 flex items-center gap-2 whitespace-nowrap ${
                  activePreviewPlatform === platform
                    ? 'border-b-2 border-primary-600 text-primary-600 font-medium'
                    : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                }`}
                aria-pressed={activePreviewPlatform === platform}
              >
                <SocialIcon platform={platform} size={18} />
              </button>
            ))}
          </div>

          {activePreviewPlatform ? (
            <div className="rounded-lg p-0">
              {/* The platform name is now part of the tab, so we might not need it duplicated here unless desired */}
              {/* <div className="flex items-center gap-2 mb-3">
                <SocialIcon platform={activePreviewPlatform} size={20} />
                <span className="text-sm font-medium text-gray-700">
                  {t('cards.preview.platformPreviewTitle', '{{platformName}} Preview', {
                    platformName: getTranslatedPlatformName(activePreviewPlatform)
                  })}
                </span>
              </div> */}
              
              <SocialMediaPostPreview
                platform={activePreviewPlatform}
                content={content}
                media={mediaObject}
                brand={brandObject}
                brandName={brandName || brandObject?.name}
                brandLogo={brandLogo || brandObject?.logo}
                format={format}
              />
            </div>
          ) : (
             // This case should ideally not be reached if displayPlatforms.length > 0 and activePreviewPlatform is set in useEffect
            <div className="text-center py-8 text-gray-500">
              {t('cards.preview.selectPlatformPrompt', 'Select a platform to see the preview')}
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-8 text-gray-500">
          {t('cards.preview.selectPlatformPrompt', 'Select a platform to see the preview')}
        </div>
      )}
    </div>
  );
};